<script setup lang="ts">
const activeTab = ref('All Stories')

const { data: blogs } = await useBlogs()
const { data: articles, status } = await useBlogList()

const tabs = computed(() => ['All Stories', ...(blogs.value ?? []).map((b) => b.title)])

const filteredArticles = computed(() => {
  if (activeTab.value === 'All Stories') return articles.value
  return articles.value.filter(
    (a: any) => a.category.toLowerCase() === activeTab.value.toLowerCase(),
  )
})

const featured = computed(() => filteredArticles.value[0] ?? null)
const gridStories = computed(() => filteredArticles.value.length > 1 ? filteredArticles.value.slice(1) : filteredArticles.value)
const storiesByCategory = computed(() => {
  const map = new Map<string, any[]>()
  for (const story of gridStories.value) {
    const cat = story.category ?? 'Uncategorized'
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(story)
  }
  return [...map.entries()].map(([category, stories]) => ({ category, stories }))
})

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

// --- Scroll-linked parallax + staggered entrance per category row ---
const sectionRef = ref<HTMLElement | null>(null)
const rowRefs = ref<HTMLElement[]>([])
// Track which cards are visible: key = `${rowIndex}-${cardIndex}`
const visibleCards = ref(new Set<string>())

// Per-row lerp state
const rowScrollState = new Map<number, { current: number; target: number; lerping: boolean }>()

function getRowState(i: number) {
  if (!rowScrollState.has(i)) rowScrollState.set(i, { current: 0, target: 0, lerping: false })
  return rowScrollState.get(i)!
}

function lerpRow(i: number) {
  const el = rowRefs.value[i]
  const state = getRowState(i)
  if (!el) return

  const diff = state.target - state.current
  if (Math.abs(diff) < 0.5) {
    state.current = state.target
    el.scrollLeft = state.current
    state.lerping = false
    return
  }
  state.current += diff * 0.08
  el.scrollLeft = state.current
  requestAnimationFrame(() => lerpRow(i))
}

function onScroll() {
  const section = sectionRef.value
  if (!section) return
  const vh = window.innerHeight

  rowRefs.value.forEach((el, i) => {
    if (!el) return
    const rect = el.getBoundingClientRect()
    const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)))
    const overflow = el.scrollWidth - el.clientWidth
    const cardWidth = el.firstElementChild?.getBoundingClientRect().width ?? 0
    const maxShift = Math.min(overflow * 0.3, cardWidth * 0.8)

    const state = getRowState(i)
    state.target = progress * maxShift
    if (!state.lerping) {
      state.lerping = true
      requestAnimationFrame(() => lerpRow(i))
    }
  })
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()

  // Staggered card entrance per row
  const observers: IntersectionObserver[] = []
  rowRefs.value.forEach((el, i) => {
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          const group = storiesByCategory.value[i]
          if (group) {
            group.stories.forEach((_: any, j: number) => {
              setTimeout(() => {
                visibleCards.value = new Set([...visibleCards.value, `${i}-${j}`])
              }, j * 80)
            })
          }
          obs.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    obs.observe(el)
    observers.push(obs)
  })

  onUnmounted(() => {
    observers.forEach((o) => o.disconnect())
    window.removeEventListener('scroll', onScroll)
  })
})
</script>

<template>
  <div>
    <!-- MASTHEAD -->
    <div
      class="bg-page flex flex-col items-center justify-center w-full"
    >
      <div
        class="flex flex-col gap-4 md:gap-6 items-center justify-center max-w-7xl w-full py-20  px-4 md:px-2 flex-1"
      >
      <div class="bg-stroke h-px w-full" />
        <p
          class="font-label font-light text-xs md:text-sm uppercase text-center tracking-[5px] text-primary"
        >
          THE MAGAZINE
        </p>
        <p
          class="font-serif font-normal text-4xl leading-normal md:text-7xl text-dark text-center"
        >
          Stories of Land, Water &amp; Craft
        </p>
        <p
          class="font-label tracking-widest font-light text-xs md:text-sm uppercase text-center text-secondary"
        >
          Beauty. Travel. Slowness.
        </p>
        <div class="bg-stroke h-px w-full" />
      </div>
    </div>

    <!-- FEATURED STORY -->
    <div class="flex flex-col items-start w-full">
      <AppSectionHeader eyebrow="LATEST STORY" class=" w-full px-4 md:px-[60px]" />

      <!-- Loading state -->
      <div
        v-if="status === 'pending'"
        class="flex items-center justify-center w-full h-[580px]"
      >
        <p class="font-label font-light text-[13px] text-brand tracking-[2px]">
          Loading stories...
        </p>
      </div>

      <!-- Featured story -->
      <div
        v-else-if="featured"
        class="flex flex-col md:flex-row md:items-stretch w-full md:h-[580px]"
      >
        <div class="relative  h-64 md:h-auto md:flex-1">
          <img
            :src="featured.image"
            :alt="featured.title"
            class="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        </div>
        <div
          class="bg-page flex flex-col gap-4 items-start justify-center px-4 py-10 md:px-20 md:py-24 md:shrink-0 md:w-1/2"
        >
          <p
            class="font-label font-light text-xs md:text-base leading-6 text-brand whitespace-nowrap"
          >
            {{ featured.category }}
          </p>
          <p
            class="font-serif font-normal text-[28px] md:text-5xl leading-9 md:leading-tight text-navy"
          >
            {{ featured.title }}
          </p>
          <div class="flex flex-col gap-2 items-start w-full">
            <div class="bg-primary h-px w-10" />
            <p
              v-if="featured.excerpt"
              class="font-sans font-normal text-sm md:text-base leading-6 text-secondary"
            >
              {{ featured.excerpt }}
            </p>
            <p class="font-sans font-light text-xs leading-4 text-muted">
              {{ featured.author }} · {{ formatDate(featured.publishedAt) }}
            </p>
          </div>
          <NuxtLink
            :to="`/journal/${featured.handle}`"
          >
            <AppButton
              type="Ghost"
              size="Small"
              label="Read the Story"
              :showIconRight="true"
            />
          </NuxtLink>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="flex items-center justify-center w-full h-[200px]">
        <p class="font-label font-light text-[13px] text-muted tracking-[2px]">
          No stories found.
        </p>
      </div>
    </div>

    <!-- STORY GRID — grouped by category, horizontal scroll per row -->
    <section
      v-if="storiesByCategory.length"
      ref="sectionRef"
      class="bg-page flex flex-col gap-8 md:gap-12 px-4 md:px-[60px] pb-12 md:pb-24 w-full"
    >
      <!-- Category tabs -->
      <div class="sticky top-0 z-30 pt-20 bg-page border-b border-stroke w-full overflow-x-auto">
        <div class="max-w-7xl mx-auto  flex gap-6 md:gap-8 min-w-max">
          <button
            v-for="tab in tabs"
            :key="tab"
            class="font-label font-light text-xs md:text-md tracking-wider uppercase py-3 border-b-2 transition-colors whitespace-nowrap"
            :class="activeTab === tab
              ? 'text-dark border-brand'
              : 'text-muted border-transparent hover:text-secondary'"
            @click="activeTab = tab; sectionRef?.scrollIntoView({ behavior: 'smooth' })"
          >
            {{ tab }}
          </button>
        </div>
      </div>
      <div
        v-for="(group, gi) in storiesByCategory"
        :key="group.category"
        class="flex flex-col gap-3 w-full"
      >
        <p class="font-label font-light text-xs md:text-base leading-4 tracking-wider text-brand uppercase">
          {{ group.category }}
        </p>
        <div
          :ref="(el: any) => { if (el) rowRefs[gi] = el }"
          class="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
        >
          <div
            v-for="(story, si) in group.stories"
            :key="story.handle"
            class="w-[75%] sm:w-[45%] lg:w-[calc(25%-12px)] flex-shrink-0"
          >
            <AppCard
              type="Feature"
              :categories="story.category"
              :title="story.title"
              :short-description="story.excerpt"
              :image="story.image"
              :slug="`/journal/${story.handle}`"
              :elevated="true"
              :tinted="true"
              :animated="true"
              :visible="visibleCards.has(`${gi}-${si}`)"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- SUBSCRIBE BAND -->
    <section
      class="hidden bg-brand flex flex-col gap-5 items-start px-4 md:px-0 py-12 md:py-24 w-full text-center"
    >
      <p
        class="font-serif font-normal italic text-lg md:text-[22px] text-white w-full"
      >
        Subscribe to the Journal — Stories delivered with the seasons.
      </p>
      <a
        href="#"
        class="font-sans font-light text-xs tracking-[2px] text-primary w-full hover:opacity-80 transition-opacity"
        >Subscribe →</a
      >
    </section>


  </div>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
